import { Trans, useTranslation } from 'react-i18next';
import HelperText from '~/components/HelperText';
import { AqarmapProSourceEnum } from '~/constants/enums';
import { useFullRoutes } from '~/hooks';
import { formatDate, formatNumber } from '~/lib';
import { PlanType } from '~/types/subscriptionTypes';
import { LocalesType } from '~/types/uiTypes';
import { sendGTMEvent } from '~/utils';

export default function PlanSummary({
  plan,
  isFreeTrial,
  toggleContent,
}: {
  plan: PlanType;
  isFreeTrial: boolean | undefined;
  toggleContent?: React.ReactNode;
}) {
  const { SUBSCRIPTION_URL } = useFullRoutes();
  const { t, i18n } = useTranslation('app');
  const locale = i18n.language;

  const hasDiscount = Boolean(plan.price_before_discount);
  const priceBeforeDiscount = plan.price_before_discount ?? 0;
  const amountOfDiscount = priceBeforeDiscount - Number(plan.price);
  const discountPercentage = Math.round(
    (amountOfDiscount / priceBeforeDiscount) * 100,
  );

  const duration = Number(plan?.duration_days) / 30;

  const getRenewDate = () => {
    const now = new Date();
    const nowDate = now.getDate();
    const renewDate = new Date(now);
    renewDate.setDate(nowDate + (plan?.trial_days ?? 0));

    return formatDate({
      value: renewDate,
      language: locale as LocalesType,
      format: 'gregorian',
    });
  };

  return (
    <>
      <div className="flex justify-between w-full mb-3x">
        <p className="text-title_4 ">{plan.title}</p>
        <button
          onClick={() => {
            sendGTMEvent({
              event: 'gtm-aqarmap_pro',
              subscribe_source: AqarmapProSourceEnum.MY_ACCOUNT,
            });
            window.location.href = SUBSCRIPTION_URL.ROOT;
          }}
          className="text-link text-blue "
        >
          {t('changePlan')}
        </button>
      </div>
      {toggleContent}

      {isFreeTrial ? (
        <div className="flex flex-col gap-x">
          <p className="text-body_2 text-black">
            <Trans
              i18nKey="freeTrial"
              t={t}
              values={{ price: 0.0, days: plan.trial_days }}
              components={{
                tag: (
                  <span className="text-caption text-gray__dark_2">
                    placeholder
                  </span>
                ),
              }}
            />
          </p>
          <p className="text-body_2 text-black">
            <Trans
              i18nKey="freeTrialStartDate"
              t={t}
              values={{
                price: formatNumber(Number(plan.price)),
                date: getRenewDate(),
              }}
              components={{
                tag: (
                  <span className="text-caption text-gray__dark_2">
                    placeholder
                  </span>
                ),
              }}
            />
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-0.5x">
          <div className="flex justify-between">
            <p className="text-body-2">{t('planPrice')}</p>
            <p className="text-caption-1 text-gray__dark_2">{`${Number(plan.price_before_discount ? plan.price_before_discount : plan.price).toLocaleString()} ${t('egp')}`}</p>
          </div>
          {hasDiscount && (
            <div className="flex justify-between mb-2x">
              <p className="text-body-2 text-green_dark ">
                {t('discountPercentage', { percentage: discountPercentage })}
              </p>
              <p className="text-caption-1 text-green_dark">
                -{amountOfDiscount?.toLocaleString()} {t('egp')}
              </p>
            </div>
          )}
        </div>
      )}
      <HelperText>
        <p className="text-orange_dark_2 text-caption-1">
          {plan.is_auto_renewal
            ? duration === 1
              ? t('automaticRenewalMonthly')
              : t('automaticRenewal', { duration })
            : t('nonAutoRenewal')}
        </p>
      </HelperText>
    </>
  );
}
